import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import authService from '../../utils/authService';

const RegistrationSuccessDialog = ({ isOpen, onClose, email, onGoToLogin }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  if (!isOpen) return null;

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage('');
    
    try {
      const result = await authService.resendVerificationEmail(email);
      if (result?.success || !result?.error) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setResendMessage('Unable to resend email. Please try again later.');
      }
    } catch {
      setResendMessage('Unable to resend email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl animate-fade-in">
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
        </div>
        
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Account Created Successfully!
          </h3>
          <p className="text-gray-600 mb-4">
            We've sent a confirmation email to{' '}
            <span className="font-medium text-gray-900">{email}</span>
          </p>
          
          <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start">
              <Icon name="Mail" size={20} className="text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Next Steps:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Check your email inbox (and spam folder)</li>
                  <li>Click the confirmation link in the email</li>
                  <li>Return here to sign in to your account</li>
                </ol>
              </div>
            </div>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start">
              <Icon name="Info" size={20} className="text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Important:</p>
                <p>You must verify your email address before you can sign in. If you don't receive the email within 5 minutes, please check your spam folder.</p>
              </div>
            </div>
          </div>
          
          {/* Resend message */}
          {resendMessage && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              resendMessage.includes('sent') 
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              <Icon 
                name={resendMessage.includes('sent') ? 'CheckCircle' : 'AlertCircle'} 
                size={16} 
                className="inline mr-2" 
              />
              {resendMessage}
            </div>
          )}
          
          <div className="space-y-3">
            <Button
              onClick={onGoToLogin}
              variant="primary"
              fullWidth
            >
              Continue to Login
            </Button>
            
            <Button
              onClick={handleResendEmail}
              variant="outline"
              fullWidth
              disabled={isResending}
              isLoading={isResending}
            >
              {isResending ? 'Sending...' : "Didn't receive email? Resend"}
            </Button>
            
            <Button
              onClick={onClose}
              variant="outline"
              fullWidth
              className="text-gray-500"
            >
              I'll verify later
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            Need help? Contact us at{' '}
            <a 
              href="mailto:support@vasprouts.com" 
              className="text-blue-600 hover:text-blue-700 underline"
            >
              support@vasprouts.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal outside component tree to prevent navigation interference
  return createPortal(modalContent, document.body);
};

export default RegistrationSuccessDialog;
